import React, { useState, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Formik } from 'formik';
import DateTimePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from 'react-datepicker';
import { enGB } from 'date-fns/locale';
import * as Yup from 'yup';
import { useSemester } from '../core/SemesterContext';
import EventService from "../core/services/EventService";
import { Icon } from '@iconify/react/dist/iconify.js';
import CustomAlert from './CustomAlert';
import Avatar from './Avatar';

import Select from 'react-select';
import useUsers from './hooks/useUsers';
import {
  getDepartmentsForContext,
  getDepartmentName,
  resolveDepartmentRef,
} from '../core/constants/departments';
import '../styles/CreateEventWizard.css';
import { useDepartments } from '../core/DepartmentsContext';
import { useAuth } from '../core/AuthContext';
import { canManageGeneralMeetings } from '../core/utils/permissionUtils';

registerLocale('en-GB', enGB);

const HR_DEPARTMENT_NAME = 'Human Resources';
const STEP_LABELS = ['Details', 'Staff', 'Review'];

function CreateEvent({ onEventCreated, onCancel }) {
  const { user: contextUser } = useAuth();
  const canManageGm = useMemo(
    () => canManageGeneralMeetings(contextUser),
    [contextUser],
  );
  const { currentSemester } = useSemester();
  const { departments: catalog } = useDepartments();

  const departmentOptions = useMemo(
    () =>
      getDepartmentsForContext('eventForm', catalog).map((dept) => ({
        value: dept._id ? String(dept._id) : dept.name,
        label: dept.name,
      })),
    [catalog]
  );

  const hrDepartment = useMemo(
    () => resolveDepartmentRef(HR_DEPARTMENT_NAME, catalog),
    [catalog]
  );
  const hrDepartmentId = hrDepartment?._id ? String(hrDepartment._id) : undefined;
  const hrDepartmentName = hrDepartment?.name ?? HR_DEPARTMENT_NAME;

  const getDepartmentLabelById = (id) =>
    departmentOptions.find((option) => option.value === String(id))?.label ?? id;

  const { users, userOptions, loading: usersLoading, formatUserDisplayName } = useUsers();

  // Special events (HR general meetings) auto-assign the Human Resources
  // department instead of showing the department picker. Staff (participants)
  // are collected the same way for every event.
  const [isSpecialEvent, setIsSpecialEvent] = useState(false);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');

  // 3-step wizard: 1 Details, 2 Staff, 3 Review
  const [step, setStep] = useState(1);

  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: ''
  });

  return (
    <div className="event-wizard-modal">
      <div className="event-wizard-header">
        <span className="event-wizard-header-title">
          <Icon icon="mdi:calendar-month-outline" />
          Create Event
        </span>
        {onCancel && (
          <button type="button" className="event-wizard-close" onClick={onCancel} aria-label="Close">
            <Icon icon="mdi:close" />
          </button>
        )}
      </div>

      <Formik
        initialValues={{
          name: '',
          date: null,
          time: null,
          meetingPoint: '',
          departments: [],
          mos: [],
          vmos: [],
          participants: [],
        }}
        validateOnChange={false}
        validateOnBlur={true}
        validationSchema={Yup.object().shape({
          name: Yup.string().required('Event name is required.'),
          date: Yup.date().nullable().required('Event date is required.'),
          time: Yup.date().nullable().required('Event time is required.'),
          meetingPoint: Yup.string(),
          departments: Yup.array().of(Yup.string()),
          mos: Yup.array().of(Yup.string()),
          vmos: Yup.array().of(Yup.string()),
          participants: Yup.array().of(Yup.string()),
        })}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          // Combine date and time before submitting
          let finalDate = values.date;
          if (values.date && values.time) {
            finalDate = new Date(values.date);
            finalDate.setHours(values.time.getHours(), values.time.getMinutes());
          }

          const submitValues = {
            ...values,
            date: finalDate,
            semester: currentSemester?._id ? String(currentSemester._id) : undefined,
            mos: isSpecialEvent ? [] : values.mos,
            vmos: isSpecialEvent ? [] : values.vmos,
            isGeneralMeeting: isSpecialEvent,
            ...(isSpecialEvent && hrDepartmentId && { departments: [hrDepartmentId] }),
          };

          EventService.createEvent(submitValues)
            .then((response) => {
              resetForm();
              setIsSpecialEvent(false);
              setStaffSearchQuery('');
              setStep(1);
              setSubmitting(false);
              onEventCreated?.(response);
            })
            .catch(error => {
              console.error('Error creating event:', error);
              setAlertInfo({
                show: true,
                type: 'error',
                message: 'There was an error creating the event. Please try again. If the error persists, contact the IT Department.'
              });
              setSubmitting(false);
            });
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setTouched, validateForm, resetForm, isSubmitting }) => {
          const goToNextStep = async () => {
            if (step === 1) {
              const stepErrors = await validateForm();
              if (stepErrors.name || stepErrors.date || stepErrors.time) {
                setTouched({ ...touched, name: true, date: true, time: true });
                return;
              }
            }
            setStep((s) => Math.min(s + 1, 3));
          };

          const goToStep = (target) => {
            if (target < step) setStep(target);
          };

          const showMoVmoFields = !isSpecialEvent;

          const handleToggleSpecialEvent = () => {
            const next = !isSpecialEvent;
            setIsSpecialEvent(next);
            if (next) {
              setFieldValue('departments', []);
              setFieldValue('mos', []);
              setFieldValue('vmos', []);
              setFieldValue('name', 'General Meeting');
              const defaultTime = new Date();
              defaultTime.setHours(21, 0, 0, 0);
              setFieldValue('time', defaultTime);
            }
          };

          const handleCancelClick = () => {
            if (onCancel) {
              onCancel();
              return;
            }
            // No parent handler (e.g. standalone /create-event page): just reset the wizard
            resetForm();
            setIsSpecialEvent(false);
            setStaffSearchQuery('');
            setStep(1);
          };

          const toggleParticipant = (userId) => {
            const isSelected = values.participants.includes(userId);
            setFieldValue(
              'participants',
              isSelected
                ? values.participants.filter((id) => id !== userId)
                : [...values.participants, userId]
            );
          };

          const filteredStaff = users.filter((user) => {
            const query = staffSearchQuery.trim().toLowerCase();
            if (!query) return true;
            return (user.fullName || '').toLowerCase().includes(query)
              || (user.nickname || '').toLowerCase().includes(query);
          });

          const dateObj = values.date;
          const monthLabel = dateObj ? dateObj.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase() : '—';
          const dayLabel = dateObj ? dateObj.getDate() : '--';
          const weekdayLabel = dateObj ? dateObj.toLocaleDateString('en-GB', { weekday: 'short' }) : '';
          const timeLabel = values.time ? values.time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '--:--';
          const dateLabel = dateObj ? dateObj.toLocaleDateString('en-GB') : '—';

          return (
            <Form onSubmit={handleSubmit}>
              <div className="event-wizard-tabs">
                {STEP_LABELS.map((label, idx) => {
                  const stepNum = idx + 1;
                  const isActive = step === stepNum;
                  const isCompleted = step > stepNum;
                  return (
                    <button
                      type="button"
                      key={label}
                      className={`event-wizard-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                      onClick={() => goToStep(stepNum)}
                      disabled={!isCompleted}
                    >
                      <span className="event-wizard-tab-circle">{isCompleted ? '✓' : stepNum}</span>
                      {label}
                    </button>
                  );
                })}
              </div>

              <div className="event-wizard-body">
                {step === 1 && (
                  <>
                    {canManageGm && (
                      <div className="event-wizard-toggle-row">
                        <div className="event-wizard-toggle-text">
                          <span className="event-wizard-toggle-label">General Meeting</span>
                          <span className="event-wizard-toggle-helper">Sets type to HR General Meeting</span>
                        </div>
                        <label className="event-wizard-switch">
                          <input type="checkbox" checked={isSpecialEvent} onChange={handleToggleSpecialEvent} />
                          <span className="event-wizard-switch-track">
                            <span className="event-wizard-switch-thumb" />
                          </span>
                        </label>
                      </div>
                    )}

                    <div className="event-wizard-field">
                      <label className="event-wizard-label" htmlFor="name">
                        Event name<span className="event-wizard-required">*</span>
                      </label>
                      <input
                        id="name"
                        className={`event-wizard-input ${touched.name && errors.name ? 'is-invalid' : ''}`}
                        type="text"
                        name="name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        placeholder="e.g. Hike to Arrábida"
                      />
                      <div className="event-wizard-field-error">{touched.name && errors.name ? errors.name : ''}</div>
                    </div>

                    <div className="event-wizard-field">
                      <label className="event-wizard-label">
                        Date &amp; time<span className="event-wizard-required">*</span>
                      </label>
                      <div className="event-wizard-date-time-grid">
                        <DateTimePicker
                          selected={values.date}
                          onChange={(date) => setFieldValue('date', date)}
                          showTimeSelect={false}
                          dateFormat="dd/MM/yyyy"
                          locale="en-GB"
                          placeholderText="dd/mm/yyyy"
                          className={`event-wizard-input ${touched.date && errors.date ? 'is-invalid' : ''}`}
                          wrapperClassName="event-wizard-picker-wrapper"
                          name="date"
                          popperProps={{ strategy: 'fixed' }}
                          portalId="root-portal"
                        />
                        <DateTimePicker
                          selected={values.time}
                          onChange={(time) => setFieldValue('time', time)}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={5}
                          timeCaption="Time"
                          dateFormat="HH:mm"
                          locale="en-GB"
                          placeholderText="00:00"
                          className={`event-wizard-input ${touched.time && errors.time ? 'is-invalid' : ''}`}
                          wrapperClassName="event-wizard-picker-wrapper"
                        />
                      </div>
                      <div className="event-wizard-field-error">
                        {(touched.date && errors.date) || (touched.time && errors.time) || ''}
                      </div>
                    </div>

                    <div className="event-wizard-field">
                      <label className="event-wizard-label" htmlFor="meetingPoint">Meeting point/location</label>
                      <input
                        id="meetingPoint"
                        className="event-wizard-input"
                        type="text"
                        name="meetingPoint"
                        placeholder="e.g. Sete Rios Station"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.meetingPoint}
                      />
                      <div className="event-wizard-field-error" />
                    </div>

                    <div className="event-wizard-field">
                      <label className="event-wizard-label">Department</label>
                      {isSpecialEvent ? (
                        <div className="event-wizard-readonly">
                          <span className="event-wizard-readonly-value">{hrDepartmentName}</span>
                          <span className="event-wizard-readonly-caption">Set by event type</span>
                        </div>
                      ) : (
                        <Select
                          isMulti
                          classNamePrefix="event-wizard-select"
                          name="departments"
                          options={departmentOptions}
                          onChange={(selectedOptions) => {
                            const vals = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                            setFieldValue('departments', vals);
                          }}
                          onBlur={handleBlur}
                          value={departmentOptions.filter((option) => values.departments.includes(option.value))}
                          placeholder="Select departments..."
                          isClearable
                          isSearchable
                        />
                      )}
                      <div className="event-wizard-field-error" />
                    </div>

                    {showMoVmoFields && (
                      <>
                        <div className="event-wizard-field">
                          <label className="event-wizard-label">Main Organizers (MOs)</label>
                          <Select
                            isMulti
                            classNamePrefix="event-wizard-select"
                            name="mos"
                            options={userOptions}
                            onChange={(selectedOptions) => {
                              const vals = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                              setFieldValue('mos', vals);
                            }}
                            onBlur={handleBlur}
                            value={userOptions.filter((option) => values.mos.includes(option.value))}
                            placeholder="Select MOs..."
                            isClearable
                            isSearchable
                            isLoading={usersLoading}
                          />
                          <div className="event-wizard-field-error" />
                        </div>

                        <div className="event-wizard-field">
                          <label className="event-wizard-label">Vice Main Organizers (VMOs)</label>
                          <Select
                            isMulti
                            classNamePrefix="event-wizard-select"
                            name="vmos"
                            options={userOptions}
                            onChange={(selectedOptions) => {
                              const vals = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                              setFieldValue('vmos', vals);
                            }}
                            onBlur={handleBlur}
                            value={userOptions.filter((option) => values.vmos.includes(option.value))}
                            placeholder="Select VMOs..."
                            isClearable
                            isSearchable
                            isLoading={usersLoading}
                          />
                          <div className="event-wizard-field-error" />
                        </div>
                      </>
                    )}
                  </>
                )}

                {step === 2 && (
                  <>
                    <p className="event-wizard-staff-intro">Staff are optional — you can add them now or later.</p>

                    <div className="event-wizard-selected-row">
                      <span className="event-wizard-selected-count">{values.participants.length} selected</span>
                      {values.participants.length > 0 && (
                        <div className="event-wizard-chip-row">
                          {values.participants.map((id) => {
                            const user = users.find((u) => u._id === id);
                            if (!user) return null;
                            const firstName = (user.fullName || user.nickname || 'User').split(' ')[0];
                            return (
                              <span key={id} className="event-wizard-chip">
                                <Avatar user={user} size={18} />
                                {firstName}
                                <button type="button" onClick={() => toggleParticipant(id)} aria-label={`Remove ${firstName}`}>✕</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="event-wizard-search-row">
                      <input
                        type="text"
                        className="event-wizard-input"
                        placeholder="Search members..."
                        value={staffSearchQuery}
                        onChange={(e) => setStaffSearchQuery(e.target.value)}
                      />
                      <div className="event-wizard-inline-actions">
                        <button
                          type="button"
                          className="event-wizard-inline-action"
                          onClick={() => setFieldValue('participants', users.map((u) => u._id))}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="event-wizard-inline-action"
                          onClick={() => setFieldValue('participants', [])}
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    {usersLoading ? (
                      <p className="event-wizard-staff-empty">Loading members...</p>
                    ) : (
                      <ul className="event-wizard-staff-list">
                        {filteredStaff.map((user) => {
                          const isSelected = values.participants.includes(user._id);
                          return (
                            <li key={user._id} className="event-wizard-staff-row">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleParticipant(user._id)}
                                />
                                <Avatar user={user} size={26} />
                                <span className="event-wizard-staff-name-block">
                                  <span className="event-wizard-staff-display-name">{formatUserDisplayName(user)}</span>
                                  <span className="event-wizard-staff-handle">{user.nickname ? `@${user.nickname}` : ''}</span>
                                </span>
                                <span className="event-wizard-staff-team">{getDepartmentName(user.departments?.[0], catalog) || ''}</span>
                              </label>
                            </li>
                          );
                        })}
                        {filteredStaff.length === 0 && (
                          <li className="event-wizard-staff-empty">No members found.</li>
                        )}
                      </ul>
                    )}
                  </>
                )}

                {step === 3 && (
                  <>
                    <div className="event-wizard-summary-card">
                      <div className="event-wizard-summary-date">
                        <span className="event-wizard-summary-month">{monthLabel}</span>
                        <span className="event-wizard-summary-day">{dayLabel}</span>
                        <span className="event-wizard-summary-weekday">{weekdayLabel}</span>
                      </div>
                      <div className="event-wizard-summary-main">
                        <div className="event-wizard-summary-name">{values.name || 'Untitled event'}</div>
                        <div className="event-wizard-summary-meta">{timeLabel} &middot; {values.meetingPoint || 'No location set'}</div>
                        <div className="event-wizard-summary-chips">
                          <span className="event-wizard-summary-chip">{isSpecialEvent ? 'HR General Meeting' : 'Regular Event'}</span>
                          <span className="event-wizard-summary-chip">
                            {isSpecialEvent ? hrDepartmentName : (getDepartmentLabelById(values.departments[0]) || 'No department')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="event-wizard-table">
                      <div className="event-wizard-table-row">
                        <span className="event-wizard-table-label">Date &amp; time</span>
                        <span className="event-wizard-table-value">{dateLabel} &middot; {timeLabel}</span>
                        <button type="button" className="event-wizard-table-edit" onClick={() => setStep(1)}>Edit</button>
                      </div>
                      <div className="event-wizard-table-row">
                        <span className="event-wizard-table-label">Meeting point</span>
                        <span className="event-wizard-table-value">
                          {values.meetingPoint || <span className="event-wizard-table-empty">Not set</span>}
                        </span>
                        <button type="button" className="event-wizard-table-edit" onClick={() => setStep(1)}>Edit</button>
                      </div>
                      {showMoVmoFields && (
                        <>
                          <div className="event-wizard-table-row">
                            <span className="event-wizard-table-label">MOs</span>
                            <span className="event-wizard-table-value">
                              {values.mos.length > 0 ? (
                                <span className="event-wizard-chip-row">
                                  {values.mos.map((id) => {
                                    const user = users.find((u) => u._id === id);
                                    if (!user) return null;
                                    return (
                                      <span key={id} className="event-wizard-chip event-wizard-chip-static">
                                        <Avatar user={user} size={16} />
                                        {formatUserDisplayName(user)}
                                      </span>
                                    );
                                  })}
                                </span>
                              ) : (
                                <span className="event-wizard-table-empty">None selected</span>
                              )}
                            </span>
                            <button type="button" className="event-wizard-table-edit" onClick={() => setStep(1)}>Edit</button>
                          </div>
                          <div className="event-wizard-table-row">
                            <span className="event-wizard-table-label">VMOs</span>
                            <span className="event-wizard-table-value">
                              {values.vmos.length > 0 ? (
                                <span className="event-wizard-chip-row">
                                  {values.vmos.map((id) => {
                                    const user = users.find((u) => u._id === id);
                                    if (!user) return null;
                                    return (
                                      <span key={id} className="event-wizard-chip event-wizard-chip-static">
                                        <Avatar user={user} size={16} />
                                        {formatUserDisplayName(user)}
                                      </span>
                                    );
                                  })}
                                </span>
                              ) : (
                                <span className="event-wizard-table-empty">None selected</span>
                              )}
                            </span>
                            <button type="button" className="event-wizard-table-edit" onClick={() => setStep(1)}>Edit</button>
                          </div>
                        </>
                      )}
                      <div className="event-wizard-table-row">
                        <span className="event-wizard-table-label">Staff</span>
                        <span className="event-wizard-table-value">
                          {values.participants.length > 0 ? (
                            <span className="event-wizard-chip-row">
                              {values.participants.map((id) => {
                                const user = users.find((u) => u._id === id);
                                if (!user) return null;
                                return (
                                  <span key={id} className="event-wizard-chip event-wizard-chip-static">
                                    <Avatar user={user} size={16} />
                                    {formatUserDisplayName(user)}
                                  </span>
                                );
                              })}
                            </span>
                          ) : (
                            <span className="event-wizard-table-empty">No staff added yet</span>
                          )}
                        </span>
                        <button type="button" className="event-wizard-table-edit" onClick={() => setStep(2)}>Edit</button>
                      </div>
                    </div>

                    <p className="event-wizard-note">
                      This event will be visible to {isSpecialEvent ? hrDepartmentName : (values.departments.map(getDepartmentLabelById).join(', ') || 'no departments yet')} and the staff added above.
                    </p>
                  </>
                )}
              </div>

              <div className="event-wizard-footer">
                {step === 1 ? (
                  <button type="button" className="event-wizard-btn-secondary" onClick={handleCancelClick}>Cancel</button>
                ) : (
                  <button type="button" className="event-wizard-btn-secondary" onClick={() => setStep((s) => s - 1)}>&larr; Back</button>
                )}

                {step < 3 && (
                  <button type="button" className="event-wizard-btn event-wizard-btn-primary" onClick={goToNextStep}>
                    Continue to {STEP_LABELS[step]}
                  </button>
                )}

                {step === 3 && (
                  <button
                    type="submit"
                    className="event-wizard-btn event-wizard-btn-success"
                    disabled={isSubmitting || usersLoading}
                  >
                    Create event
                  </button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss={true}
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
        />
      )}
    </div>
  );
}

export default CreateEvent;
